import { DEV, launchDarklyClientSideId, resolveEnv } from '@fluentsms/utils';
import { ProviderConfig } from 'launchdarkly-react-client-sdk';

export const environment = resolveEnv();
export const clientSideID = launchDarklyClientSideId[environment];

export const launchDarklyConfig: ProviderConfig = {
  clientSideID,
  options: {
    bootstrap: 'localStorage',
  },
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
  deferInitialization: false,
};

/**
 * FEATURE FLAG NAMING CONVENTION:
 * Ensure that feature flag names are valid in one of the following forms:
 *
 * Format 1 (Legacy): documents-1789856-abc123-def456
 *   WHERE:
 *     documents = owner of the feature flag
 *     1789856 = ticket id of the feature flag (numeric)
 *     abc123-def456 = kabob case description of the feature flag
 *
 * Format 2 (New): title-anp-2187-contact-verification
 *   WHERE:
 *     title = owner of the feature flag
 *     anp-2187 = ticket id with project prefix (e.g., ANP, JIRA, etc.)
 *     contact-verification = kabob case description of the feature flag
 *
 */
export const validateFeatureFlagName = (name: string) => {
  // Legacy format: owner-######-description (e.g., documents-1789856-abc123-def456)
  const legacyFormat = /^[a-z]+-\d+(-[a-z0-9]+)+$/;

  // New format: owner-project-####-description (e.g., title-anp-2187-contact-verification)
  const newFormat = /^[a-z]+-[aA-zZ]+-\d+(-[a-z0-9]+)+$/;

  // TODO(ANP-6556): Rename this existing LaunchDarkly key end-to-end, then remove the exception.
  const allowedFeatureFlags = new Set(['underwriters-6490-fidelity-Integration']);

  const isValid = allowedFeatureFlags.has(name) || legacyFormat.test(name) || newFormat.test(name);
  const errorMessage = `Invalid feature flag name: ${name}. Expected format: 'owner-ticket-description' (e.g., 'documents-1789856-feature-name' or 'title-anp-2187-feature-name')`;

  if (!isValid) {
    console.warn(errorMessage);
    if (environment === DEV) throw new Error(errorMessage);
  }
};

/**
 * Feature flag for merged Steps 3 & 4 UI redesign (Recording Fees + Transfer Taxes).
 * Enables card-wrapped sections with inline totals and sticky footer bar.
 * Format: settlements-anp-8444-steps-3-4-merge
 */
export const RFTT_STEPS_3_4_MERGE = 'settlements-anp-8444-steps-3-4-merge';

/**
 * Feature flag for the tenant switcher in the app header.
 * On: the current tenant renders beside the profile icon and opens a searchable tenant list.
 * Off: the tenant name and Switch Tenant action stay in the profile menu.
 * Format: platform-anp-8181-header-tenant-switcher
 */
export const HEADER_TENANT_SWITCHER = 'platform-anp-8181-header-tenant-switcher';
